import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import duration from 'dayjs/plugin/duration';
import isBetween from 'dayjs/plugin/isBetween';
import objectSupport from 'dayjs/plugin/objectSupport';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import pluralize from 'pluralize';

dayjs.extend(customParseFormat);
dayjs.extend(duration);
dayjs.extend(isBetween);
dayjs.extend(objectSupport);
dayjs.extend(relativeTime);
dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Returns a human-friendly due-date label relative to today.
 *
 * Examples:
 *  - "Due today"
 *  - "Due tomorrow"
 *  - "Due in 12 days"
 *  - "Due in 2 months"
 *  - "Due in 2 months and 5 days"
 *  - "Due yesterday"
 *  - "3 days past due"
 *  - "2 months and 4 days past due"
 */
export function friendlyDueDate(dueDate, format = 'M/D/YYYY') {
  const today = dayjs().startOf('day');

  const due = typeof dueDate === 'string' ? dayjs(dueDate, format, true).startOf('day') : dayjs(dueDate).startOf('day');

  if (!due.isValid()) {
    return null;
  }

  const dayDiff = due.diff(today, 'day');

  // --- TODAY / TOMORROW ---
  if (dayDiff === 0) return 'Due today';
  if (dayDiff === 1) return 'Due tomorrow';

  // --- FUTURE ---
  if (dayDiff > 1) {
    const months = due.diff(today, 'month');

    if (months === 0) {
      return `Due in ${dayDiff} ${pluralize('day', dayDiff)}`;
    }

    const todayPlusMonths = today.add(months, 'month');
    const daysRemainder = due.diff(todayPlusMonths, 'day');

    if (daysRemainder === 0) {
      return `Due in ${months} ${pluralize('month', months)}`;
    }

    return `Due in ${months} ${pluralize('month', months)} and ${daysRemainder} ${pluralize('day', daysRemainder)}`;
  }

  // --- PAST DUE ---
  if (dayDiff === -1) return 'Due yesterday';

  const overdueDays = Math.abs(dayDiff);
  const monthsOver = today.diff(due, 'month');

  if (monthsOver === 0) {
    return `${overdueDays} ${pluralize('day', overdueDays)} past due`;
  }

  const duePlusMonths = due.add(monthsOver, 'month');
  const daysRemainder = today.diff(duePlusMonths, 'day');

  if (daysRemainder === 0) {
    return `${monthsOver} ${pluralize('month', monthsOver)} past due`;
  }

  return `${monthsOver} ${pluralize('month', monthsOver)} and ${daysRemainder} ${pluralize('day', daysRemainder)} past due`;
}

function makeFriendlyDate(timestamp) {
  let dateString;

  if (!timestamp) {
    return timestamp;
  }

  if (!dayjs.isDayjs(timestamp)) {
    timestamp = dayjs(timestamp);
  }

  const today = dayjs();

  if (timestamp.isSame(today, 'day')) {
    dateString = 'Today';
  } else if (today.subtract(1, 'day').isSame(timestamp, 'day')) {
    dateString = 'Yesterday';
  } else {
    dateString = timestamp.format('M/D/YYYY');
  }

  return `${dateString}`;
}

function makeFriendlyTimestamp(timestamp) {
  if (!timestamp) {
    return timestamp;
  }

  const dateString = makeFriendlyDate(timestamp);

  if (!dayjs.isDayjs(timestamp)) {
    timestamp = dayjs(timestamp);
  }

  return `${dateString} ${timestamp.format('h:mm A')}`;
}

/**
 * Formats a given date string to M/D/YYYY format.
 *
 * @param {string} dateString
 */
function monthDayYear(dateString) {
  return dayjs(dateString).format('M/D/YYYY');
}

/**
 * Returns a Dayjs instance using the current date/time.
 *
 * @returns {Dayjs} Dayjs instance
 */
function now() {
  return dayjs();
}

/**
 * Converts the given seconds to a days/hours/minutes phrase.
 *
 * @param {number} seconds
 */
function secondsToDaysHoursMinutes(seconds, includeSeconds = false) {
  const SECONDS_PER_MINUTE = 60;
  const SECONDS_PER_HOUR = SECONDS_PER_MINUTE * 60;
  const SECONDS_PER_DAY = SECONDS_PER_HOUR * 24;
  const days = Math.floor(seconds / SECONDS_PER_DAY);
  const hours = Math.floor((seconds - days * SECONDS_PER_DAY) / SECONDS_PER_HOUR);
  const minutes = Math.floor((seconds - (hours * SECONDS_PER_HOUR + days * SECONDS_PER_DAY)) / SECONDS_PER_MINUTE);
  const remainingSeconds = seconds - (hours * SECONDS_PER_HOUR + days * SECONDS_PER_DAY + minutes * SECONDS_PER_MINUTE);

  const daysHoursMins = [];

  if (days > 0) {
    daysHoursMins.push(`${days}${getLabel(days, 'day')}`);
  }

  if (hours > 0) {
    daysHoursMins.push(`${hours}${getLabel(hours, 'hr')}`);
  }

  if (minutes > 0) {
    daysHoursMins.push(`${minutes}${getLabel(minutes, 'min')}`);
  }

  if (includeSeconds && remainingSeconds) {
    daysHoursMins.push(`${remainingSeconds}${getLabel(remainingSeconds, 'sec')}`);
  }

  return daysHoursMins.join(' ');

  function getLabel(value, label) {
    if (value > 1) {
      return ` ${label}s`;
    }
    if (value === 1) {
      return ` ${label}`;
    }
    return '';
  }
}

/**
 * Converts the given seconds to an hours and minutes phrase.
 *
 * @param {number} seconds
 * @returns {string} x_hr(s) x_min(s)
 */
function secondsToHoursMinutes(seconds) {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.round((seconds - hours * 3600) / 60);

  const hoursAndMins = [];

  if (hours > 0) {
    hoursAndMins.push(`${hours}${getLabel(hours, 'hr')}`);
  }

  if (minutes > 0) {
    hoursAndMins.push(`${minutes}${getLabel(minutes, 'min')}`);
  }

  if (hours === 0 && minutes === 0) {
    const intSeconds = Math.floor(seconds);
    hoursAndMins.push(`${intSeconds}${getLabel(intSeconds, 'second')}`);
  }

  if (hours === 0 && minutes === 0 && seconds === 0) {
    return '0 seconds';
  }

  return hoursAndMins.join(' ');

  function getLabel(value, label) {
    if (value > 1) {
      return ` ${label}s`;
    }
    if (value === 1) {
      return ` ${label}`;
    }
    return '';
  }
}

/**
 * Converts the given seconds to a days/hours/minutes phrase.
 *
 * @param {String} timestamp1
 * @param {String} timestamp2
 * @returns {String} x_day(s) x_hr(s) x_min(s)
 */
function timestampsToDaysHoursMinutes(ts1, ts2) {
  const start = dayjs(ts1);
  const end = dayjs(ts2);
  const durationInSeconds = end.diff(start, 'second');
  return secondsToDaysHoursMinutes(durationInSeconds);
}

/**
 * Returns a Dayjs object using the start of today's date.
 *
 * @returns {Dayjs}
 */
function todayStart() {
  return dayjs().startOf('day');
}

/**
 * Returns a Dayjs object using the end of today's date.
 *
 * @returns {Dayjs}
 */
function todayEnd() {
  return dayjs().endOf('day');
}

/**
 * Converts the given UTC timestamp into a formatted local time string.
 * @todo Write tests for this
 *
 * @param {string | number | import('dayjs').Dayjs} utcTimestamp
 * @param {boolean} includeSeconds
 */
function utcToLocalDateTime(utcTimestamp, includeSeconds = false) {
  let dateString;

  if (!utcTimestamp) {
    return utcTimestamp;
  }

  const ts =
    typeof utcTimestamp === 'string' || typeof utcTimestamp === 'number'
      ? dayjs.utc(utcTimestamp).local()
      : utcTimestamp;

  const today = dayjs();

  if (ts.isSame(today, 'day')) {
    dateString = 'Today';
  } else if (today.subtract(1, 'day').isSame(ts, 'day')) {
    dateString = 'Yesterday';
  } else {
    dateString = ts.format('M/D/YY');
  }

  return `${dateString} ${ts.format(`h:mm${includeSeconds ? ':ss' : ''} A`)}`;
}

/**
 * Converts a given UTC time into a local time "h:mm A" format.
 *
 * @param {string} timeString
 */
function utcToLocalTimestamp(timeString, includeSeconds = false) {
  return dayjs
    .utc(timeString)
    .local()
    .format(`h:mm${includeSeconds ? ':ss' : ''} A`);
}

export {
  dayjs,
  makeFriendlyDate,
  makeFriendlyTimestamp,
  monthDayYear,
  now,
  secondsToDaysHoursMinutes,
  secondsToHoursMinutes,
  timestampsToDaysHoursMinutes,
  todayStart,
  todayEnd,
  utcToLocalDateTime,
  utcToLocalTimestamp,
};
